import React, { useEffect, useMemo, useState } from "react";
import {
  FiUser,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiHome,
  FiUserPlus,
  FiSettings,
  FiMapPin,
  FiPhone,
  FiMail,
  FiCalendar,
  FiCreditCard,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import "./GeneralUser.css";

const GeneralUser = () => {
  const navigate = useNavigate();
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  const [roleRequests, setRoleRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [reqRes, propRes] = await Promise.all([
          api.get("/api/my-role-requests"),
          api.get("/api/get-properties"),
        ]);
        if (cancelled) return;
        setRoleRequests(reqRes.data?.data || reqRes.data || []);
        const raw = propRes.data?.data || propRes.data || [];
        setProperties(Array.isArray(raw) ? raw.slice(0, 6) : []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("Failed to load dashboard data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userData = {
    name: storedUser?.name || "User",
    email: storedUser?.email || "—",
    phone: storedUser?.mobile_number || storedUser?.phone || "—",
    location: storedUser?.address || "Bangladesh",
    joinDate: storedUser?.created_at
      ? new Date(storedUser.created_at).toLocaleDateString()
      : "—",
    profileComplete: [
      storedUser?.name,
      storedUser?.email,
      storedUser?.mobile_number,
      storedUser?.nid,
    ].filter(Boolean).length * 25,
  };

  const getStatusColor = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return "#10b981";
      case "rejected":
        return "#ef4444";
      case "pending":
      case "under_review":
        return "#f59e0b";
      default:
        return "#64748b";
    }
  };

  const getStatusIcon = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "approved":
        return <FiCheckCircle />;
      case "rejected":
        return <FiXCircle />;
      case "pending":
      case "under_review":
        return <FiClock />;
      default:
        return <FiAlertCircle />;
    }
  };

  const normalizeRequest = (request) => {
    const roleName =
      request.role?.name ||
      request.requested_role ||
      request.role_name ||
      "Role";
    const status = request.status || request.approval_status || "pending";
    const submitted =
      request.created_at || request.submitted_at || request.submittedDate;
    return {
      id: request.id,
      requestedRole: roleName,
      submittedDate: submitted
        ? new Date(submitted).toLocaleDateString()
        : "—",
      status,
      applicationId: `APP-${request.id}`,
      company: request.company?.name || "",
    };
  };

  return (
    <div className="gu-dashboard-container">
      <div className="gu-welcome-section">
        <div className="gu-welcome-card">
          <div className="gu-welcome-content">
            <div className="gu-user-avatar">
              {userData.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)}
            </div>
            <div className="gu-welcome-text">
              <h1>Welcome back, {userData.name.split(" ")[0]}!</h1>
              <p>
                Track your role applications and browse available properties.
              </p>
            </div>
          </div>

          <div className="gu-profile-completion">
            <div className="gu-completion-header">
              <span>Profile Completion</span>
              <strong>{userData.profileComplete}%</strong>
            </div>
            <div className="gu-progress-bar">
              <div
                className="gu-progress-fill"
                style={{ width: `${userData.profileComplete}%` }}
              ></div>
            </div>
            <p className="gu-completion-hint">
              Complete your profile to increase your chances of role approval
            </p>
          </div>
        </div>

        <div className="gu-user-info-grid">
          <div className="gu-info-card">
            <FiMail className="gu-info-icon" />
            <div className="gu-info-content">
              <span className="gu-info-label">Email</span>
              <span className="gu-info-value">{userData.email}</span>
            </div>
          </div>
          <div className="gu-info-card">
            <FiPhone className="gu-info-icon" />
            <div className="gu-info-content">
              <span className="gu-info-label">Phone</span>
              <span className="gu-info-value">{userData.phone}</span>
            </div>
          </div>
          <div className="gu-info-card">
            <FiMapPin className="gu-info-icon" />
            <div className="gu-info-content">
              <span className="gu-info-label">Location</span>
              <span className="gu-info-value">{userData.location}</span>
            </div>
          </div>
          <div className="gu-info-card">
            <FiCalendar className="gu-info-icon" />
            <div className="gu-info-content">
              <span className="gu-info-label">Joined</span>
              <span className="gu-info-value">{userData.joinDate}</span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      <div className="gu-role-request-section">
        <h2 className="gu-section-title">Your Role Application Status</h2>

        {loading ? (
          <p>Loading applications…</p>
        ) : roleRequests.length > 0 ? (
          <div className="gu-requests-list">
            {roleRequests.map((raw) => {
              const request = normalizeRequest(raw);
              return (
                <div className="gu-request-card" key={request.id}>
                  <div className="gu-request-header">
                    <div className="gu-request-title">
                      <h3>Application for {request.requestedRole}</h3>
                      <span className="gu-application-id">
                        ID: {request.applicationId}
                        {request.company ? ` • ${request.company}` : ""}
                      </span>
                    </div>
                    <div
                      className="gu-status-badge"
                      style={{
                        backgroundColor: `${getStatusColor(request.status)}15`,
                        color: getStatusColor(request.status),
                      }}
                    >
                      {getStatusIcon(request.status)}
                      <span>{String(request.status).replace(/_/g, " ")}</span>
                    </div>
                  </div>
                  <div className="gu-request-info">
                    <div className="gu-info-item">
                      <FiCalendar size={16} />
                      <span>Submitted: {request.submittedDate}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="gu-no-requests">
            <FiUser size={48} />
            <h3>No Active Applications</h3>
            <p>You haven't applied for any roles yet.</p>
            <Link to="/role_selection" className="gu-apply-btn">
              <FiUserPlus /> Apply for a Role
            </Link>
          </div>
        )}
      </div>

      <div className="gu-quick-actions-section">
        <h2 className="gu-section-title">Quick Actions</h2>
        <div className="gu-actions-grid">
          <button
            className="gu-action-card"
            onClick={() => navigate("/role_selection")}
          >
            <FiUserPlus />
            <strong>Apply for Role</strong>
            <span>Submit application for available positions</span>
          </button>
          <button
            className="gu-action-card"
            onClick={() => navigate("/my-payments")}
          >
            <FiCreditCard />
            <strong>My Payments</strong>
            <span>Bookings, receipts, and installment schedule</span>
          </button>
          <button
            className="gu-action-card"
            onClick={() => navigate("/greenland/properties")}
          >
            <FiHome />
            <strong>View Properties</strong>
            <span>Browse our property listings</span>
          </button>
          <button
            className="gu-action-card"
            onClick={() => navigate("/profile")}
          >
            <FiSettings />
            <strong>Update Profile</strong>
            <span>Complete your profile information</span>
          </button>
          <button
            className="gu-action-card"
            onClick={() => navigate("/contact-us")}
          >
            <FiPhone />
            <strong>Contact Support</strong>
            <span>Get help from our team</span>
          </button>
        </div>
      </div>

      <div className="gu-properties-section">
        <h2 className="gu-section-title">Featured Properties</h2>
        <div className="gu-properties-grid">
          {properties.map((property) => (
            <Link
              key={property.id}
              to={`/greenland/properties/${property.id}`}
              className="gu-property-card"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div className="gu-property-image">🏢</div>
              <div className="gu-property-content">
                <h4>{property.name}</h4>
                <p>
                  {property.location?.area || property.location?.city || "—"}
                </p>
                <span>{property.type?.name || "Property"}</span>
              </div>
            </Link>
          ))}
          {!loading && properties.length === 0 && (
            <p className="text-muted">No properties available right now.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneralUser;
